import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O que é sinastria astrológica?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sinastria é a técnica astrológica que compara dois mapas natais para avaliar a compatibilidade entre duas pessoas. Ela analisa como os planetas de uma pessoa se relacionam com os planetas da outra, revelando pontos de harmonia, atração, conflito e potencial de crescimento no relacionamento.',
      },
    },
    {
      '@type': 'Question',
      name: 'Como saber se dois signos são compatíveis?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A compatibilidade entre signos vai muito além do signo solar. Uma análise completa considera a posição do Sol, da Lua, de Vênus, de Marte e do Ascendente de cada pessoa. Dois signos considerados "incompatíveis" podem ter uma sinastria muito harmoniosa com base nas demais posições planetárias.',
      },
    },
    {
      '@type': 'Question',
      name: 'Compatibilidade astrológica serve para relacionamentos não românticos?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sim. A sinastria pode ser usada para analisar qualquer tipo de relação: amizades, relações entre colegas de trabalho, sócios de negócios ou membros da família. Os aspectos entre os mapas revelam a dinâmica de qualquer vínculo interpessoal.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quais aspectos astrológicos indicam compatibilidade?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Aspectos como conjunções, trígonos e sextis entre planetas de pessoas diferentes geralmente indicam harmonia e facilidade de conexão. Quadraturas e oposições não são necessariamente negativas, elas criam tensão criativa e atração poderosa, mas podem exigir mais esforço para manter o equilíbrio.',
      },
    },
    {
      '@type': 'Question',
      name: 'O que é o composto astrológico (Composite Chart)?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O mapa composto é um mapa criado a partir da média matemática dos pontos nos mapas de duas pessoas. Ele representa a "entidade" do relacionamento em si, não os indivíduos, mas o que a relação cria entre eles. É complementar à sinastria e oferece uma visão do propósito e da natureza do vínculo.',
      },
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://professya.com' },
    { '@type': 'ListItem', position: 2, name: 'Compatibilidade Astrológica', item: 'https://professya.com/compatibilidade-astrologica' },
  ],
};

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Compatibilidade Astrológica: Guia Completo de Sinastria',
  description: 'Aprenda o que é sinastria, como a compatibilidade astrológica é calculada e como usar o seu mapa natal para entender seus relacionamentos.',
  author: { '@type': 'Organization', name: 'Professya' },
  publisher: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://professya.com/compatibilidade-astrologica' },
};

export function CompatibilidadeAstrologicaPage() {
  return (
    <>
      <Helmet>
        <title>Compatibilidade Astrológica: Sinastria Online | Professya</title>
        <meta name="description" content="Descubra sua compatibilidade astrológica com qualquer pessoa. Análise completa de sinastria baseada nos seus mapas natais: amor, amizade, trabalho e mais." />
        <meta name="keywords" content="compatibilidade astrológica, sinastria, compatibilidade de signos, mapa natal casal, sinastria online, compatibilidade astral" />
        <link rel="canonical" href="https://professya.com/compatibilidade-astrologica" />
        <meta property="og:title" content="Compatibilidade Astrológica: Sinastria Online | Professya" />
        <meta property="og:description" content="Análise profunda de sinastria para descobrir a compatibilidade real entre você e outra pessoa." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://professya.com/compatibilidade-astrologica" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div style={{ background: '#0D1130', minHeight: '100vh', color: '#F5ECD7' }}>
        {/* Hero */}
        <section style={{ background: 'linear-gradient(180deg, #0D1130 0%, #13193F 100%)', padding: '4rem 1.5rem 3rem', textAlign: 'center' }}>
          <nav style={{ fontSize: '13px', color: 'rgba(245,236,215,0.45)', marginBottom: '2rem' }}>
            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
            {' › '}
            <span style={{ color: '#C9A84C' }}>Compatibilidade Astrológica</span>
          </nav>
          <p style={{ color: '#C9A84C', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '1rem' }}>Sinastria</p>
          <h1 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: 300, lineHeight: 1.25, margin: '0 auto 1.25rem', maxWidth: '720px' }}>
            Compatibilidade Astrológica:<br />O que os astros dizem sobre seus relacionamentos
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(245,236,215,0.7)', maxWidth: '560px', margin: '0 auto 2rem', lineHeight: 1.65 }}>
            A sinastria vai muito além da comparação de signos. Ela revela a dinâmica profunda entre dois mapas natais, amor, amizade, trabalho e família.
          </p>
          <Link to="/register" style={{ display: 'inline-block', padding: '0.875rem 2.25rem', background: '#C9A84C', color: '#0D1130', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none' }}>
            Analisar minha compatibilidade
          </Link>
        </section>

        <div style={{ maxWidth: '780px', margin: '0 auto', padding: '3rem 1.5rem' }}>

          {/* What is sinastry */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.6rem', fontWeight: 300, color: '#C9A84C', marginBottom: '1rem' }}>
              O que é sinastria astrológica?
            </h2>
            <p style={{ color: 'rgba(245,236,215,0.8)', lineHeight: 1.8, marginBottom: '1rem' }}>
              A <strong>sinastria</strong> é a técnica astrológica mais usada para avaliar a compatibilidade entre duas pessoas. Ela funciona sobrepondo dois mapas natais e analisando como os planetas de uma pessoa formam aspectos com os planetas da outra.
            </p>
            <p style={{ color: 'rgba(245,236,215,0.8)', lineHeight: 1.8, marginBottom: '1rem' }}>
              Diferente da simples comparação de signos solares ("Ária e Sagitário combinam?"), a sinastria considera mais de 20 pontos planetários em cada mapa, Sol, Lua, Mercúrio, Vênus, Marte, Júpiter, Saturno, Urano, Netuno, Plutão, Quiron e o Ascendente, entre outros.
            </p>
            <p style={{ color: 'rgba(245,236,215,0.8)', lineHeight: 1.8 }}>
              O resultado é uma análise detalhada da <strong>dinâmica real do relacionamento</strong>: onde há atração, onde há fricção, onde há suporte mútuo e onde podem surgir desafios de longo prazo.
            </p>
          </section>

          {/* How it works */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.6rem', fontWeight: 300, color: '#C9A84C', marginBottom: '1rem' }}>
              Como a compatibilidade astrológica é calculada?
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
              {[
                { icon: '☀️', title: 'Sol ↔ Lua', desc: 'Indica a harmonia emocional e o ritmo de vida entre as duas pessoas.' },
                { icon: '♀️', title: 'Vênus ↔ Marte', desc: 'Revela a atração romântica e sexual, o desejo e a química física.' },
                { icon: '🌙', title: 'Lua ↔ Lua', desc: 'Analisa o conforto emocional e a facilidade de comunicar sentimentos.' },
                { icon: '☿', title: 'Mercúrio ↔ planetas', desc: 'Mostra como as duas pessoas se comunicam e compreendem uma à outra.' },
                { icon: '⊕', title: 'Ascendente ↔ planetas', desc: 'Como cada pessoa percebe e impacta a presença física da outra.' },
                { icon: '♄', title: 'Saturno ↔ planetas', desc: 'Indica comprometimento, responsabilidade e durabilidade do vínculo.' },
              ].map(item => (
                <div key={item.title} style={{ padding: '1.25rem', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '4px', background: 'rgba(201,168,76,0.03)' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                  <p style={{ fontWeight: 600, color: '#F5ECD7', fontSize: '0.9rem', marginBottom: '0.4rem' }}>{item.title}</p>
                  <p style={{ color: 'rgba(245,236,215,0.6)', fontSize: '0.85rem', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Aspects */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.6rem', fontWeight: 300, color: '#C9A84C', marginBottom: '1rem' }}>
              Aspectos harmoniosos vs. desafiadores
            </h2>
            <p style={{ color: 'rgba(245,236,215,0.8)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
              Na sinastria, os aspectos entre planetas indicam a qualidade da energia entre as duas pessoas naquele tema específico:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ padding: '1.25rem', border: '1px solid rgba(74,222,128,0.2)', borderRadius: '4px', background: 'rgba(74,222,128,0.04)' }}>
                <p style={{ color: '#4ade80', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Harmoniosos</p>
                <ul style={{ color: 'rgba(245,236,215,0.7)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li><strong>Conjunção</strong>, fusão de energias, intensidade</li>
                  <li><strong>Trígono</strong>, fluxo natural, facilidade</li>
                  <li><strong>Sextil</strong>, cooperação, oportunidades</li>
                </ul>
              </div>
              <div style={{ padding: '1.25rem', border: '1px solid rgba(251,146,60,0.2)', borderRadius: '4px', background: 'rgba(251,146,60,0.04)' }}>
                <p style={{ color: '#fb923c', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Desafiadores</p>
                <ul style={{ color: 'rgba(245,236,215,0.7)', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li><strong>Quadratura</strong>, tensão criativa, atrito</li>
                  <li><strong>Oposição</strong>, polaridade, atração magnética</li>
                  <li><strong>Quincúncio</strong>, desajuste, ajustes constantes</li>
                </ul>
              </div>
            </div>
            <p style={{ color: 'rgba(245,236,215,0.6)', fontSize: '0.875rem', marginTop: '1rem', lineHeight: 1.7 }}>
              <em>Atenção: aspectos desafiadores não significam incompatibilidade, eles criam a tensão necessária para crescimento e aprendizado mútuo. Os relacionamentos mais duradouros geralmente têm uma mistura saudável de harmonia e desafio.</em>
            </p>
          </section>

          {/* Sign compatibility table */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.6rem', fontWeight: 300, color: '#C9A84C', marginBottom: '1rem' }}>
              Compatibilidade por elemento
            </h2>
            <p style={{ color: 'rgba(245,236,215,0.8)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
              Como regra geral, e apenas como ponto de partida, signos do mesmo elemento tendem a se entender com mais facilidade:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '1rem' }}>
              {[
                { element: 'Fogo 🔥', signs: 'Áries, Leão, Sagitário', traits: 'Entusiastas, apaixonados, diretos' },
                { element: 'Terra 🌿', signs: 'Touro, Virgem, Capricórnio', traits: 'Práticos, estáveis, construtivos' },
                { element: 'Ar 💨', signs: 'Gêmeos, Libra, Aquário', traits: 'Intelectuais, comunicativos, sociais' },
                { element: 'Água 💧', signs: 'Câncer, Escorpião, Peixes', traits: 'Emocionais, intuitivos, profundos' },
              ].map(e => (
                <div key={e.element} style={{ padding: '1rem', border: '1px solid rgba(201,168,76,0.12)', borderRadius: '4px' }}>
                  <p style={{ fontWeight: 600, color: '#C9A84C', fontSize: '0.9rem', marginBottom: '0.35rem' }}>{e.element}</p>
                  <p style={{ color: '#F5ECD7', fontSize: '0.85rem', marginBottom: '0.3rem' }}>{e.signs}</p>
                  <p style={{ color: 'rgba(245,236,215,0.5)', fontSize: '0.8rem', lineHeight: 1.5 }}>{e.traits}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section style={{ textAlign: 'center', padding: '2.5rem', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '4px', background: 'rgba(201,168,76,0.03)', marginBottom: '3rem' }}>
            <p style={{ color: '#C9A84C', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Análise personalizada</p>
            <h3 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.4rem', fontWeight: 300, color: '#F5ECD7', marginBottom: '1rem' }}>
              Descubra sua compatibilidade real
            </h3>
            <p style={{ color: 'rgba(245,236,215,0.65)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: '460px', margin: '0 auto 1.5rem' }}>
              Insira os dados de nascimento de ambas as pessoas e receba uma análise profunda de sinastria baseada em todos os planetas dos dois mapas.
            </p>
            <Link to="/register" style={{ display: 'inline-block', padding: '0.875rem 2rem', background: '#C9A84C', color: '#0D1130', fontWeight: 700, fontSize: '0.875rem', letterSpacing: '0.07em', textTransform: 'uppercase', textDecoration: 'none', marginRight: '1rem' }}>
              Começar gratuitamente
            </Link>
            <Link to="/login" style={{ display: 'inline-block', padding: '0.875rem 1.5rem', border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', fontWeight: 500, fontSize: '0.875rem', textDecoration: 'none' }}>
              Já tenho conta
            </Link>
          </section>

          {/* FAQ */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontSize: '1.6rem', fontWeight: 300, color: '#C9A84C', marginBottom: '1.5rem' }}>
              Perguntas frequentes
            </h2>
            {faqSchema.mainEntity.map((item) => (
              <details key={item.name} style={{ marginBottom: '0.75rem', border: '1px solid rgba(201,168,76,0.12)', borderRadius: '4px', padding: '1rem 1.25rem' }}>
                <summary style={{ color: '#F5ECD7', fontWeight: 500, fontSize: '0.95rem', cursor: 'pointer', lineHeight: 1.5 }}>
                  {item.name}
                </summary>
                <p style={{ color: 'rgba(245,236,215,0.7)', fontSize: '0.9rem', lineHeight: 1.75, marginTop: '0.75rem', marginBottom: 0 }}>
                  {item.acceptedAnswer.text}
                </p>
              </details>
            ))}
          </section>

          {/* Related links */}
          <nav aria-label="Páginas relacionadas" style={{ borderTop: '1px solid rgba(201,168,76,0.12)', paddingTop: '2rem' }}>
            <p style={{ color: 'rgba(245,236,215,0.45)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem' }}>Conteúdo relacionado</p>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              {[
                { to: '/mapa-natal', label: 'Mapa Natal' },
                { to: '/ascendente', label: 'Ascendente' },
                { to: '/taro', label: 'Tarot Online' },
                { to: '/retrogrado', label: 'Mercúrio Retrógrado' },
              ].map(l => (
                <Link key={l.to} to={l.to} style={{ color: '#C9A84C', fontSize: '0.875rem', textDecoration: 'none', borderBottom: '1px solid rgba(201,168,76,0.25)', paddingBottom: '2px' }}>
                  {l.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
